<!-- 
화면명 : FList.vue
작성일 : 2026-08-17
설명   : 첨부파일 목록 및 뷰어(이미지/PDF) 팝업 화면
-->
<template>
  <div class="p-3 bg-slate-100 text-slate-800 min-h-screen select-none font-sans text-xs sm:text-sm flex flex-col h-screen">
    <!-- 상단 헤더 영역 -->
    <div class="flex justify-between items-center pb-2 flex-shrink-0 bg-white p-3 rounded-lg shadow-sm border border-gray-200 mb-2">
      <div class="flex items-center space-x-2">
        <span class="inline-block w-3.5 h-3.5 rounded-full bg-blue-600 border-2 border-blue-200"></span>
        <span class="font-bold text-base sm:text-lg text-slate-900">{{ titleText }}</span>
        <span v-if="currentGuid" class="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded border border-blue-200 font-mono">
          GUID: {{ currentGuid }}
        </span>
      </div>

      <!-- 상단 액션 버튼 그룹 -->
      <div class="flex items-center space-x-2">
        <!-- 파일 선택용 숨김 input -->
        <input
          type="file"
          ref="fileInputRef"
          :accept="acceptFileExtensions"
          multiple
          @change="handleFileSelected"
          class="hidden"
        />

        <button
          v-if="canEdit"
          @click="triggerFileInput"
          :disabled="isUploading"
          class="btn-action bg-blue-50 text-blue-700 border-blue-300 hover:bg-blue-100 disabled:opacity-50"
          :title="`파일 업로드 (${allowedExtsLabel}만 가능, 최대 ${maxUploadSizeMb}MB)`"
        >
          <i class="fa fa-upload mr-1 text-blue-600"></i>
          {{ isUploading ? '업로드 중...' : '파일 추가' }}
        </button>

        <button
          @click="fetchFileList"
          class="btn-action"
          title="새로고침"
        >
          <i class="fa fa-sync-alt mr-1 text-blue-600"></i> 새로고침
        </button>
        <button
          v-if="fileList.length > 0"
          @click="downloadAllFiles"
          class="btn-action"
          title="전체 다운로드"
        >
          <i class="fa fa-download mr-1 text-green-600"></i> 전체 다운로드
        </button>
        <button
          @click="btnClose"
          class="btn-action text-red-600 border-red-200 hover:bg-red-50"
        >
          <i class="fa fa-times mr-1 text-red-500"></i> 닫기
        </button>
      </div>
    </div>

    <!-- 본문 스플릿 뷰 영역 (좌측: 파일 그리드 목록, 우측: 파일 뷰어) -->
    <div class="flex-1 flex gap-3 min-h-0 overflow-hidden">
      <!-- 좌측: 파일 목록 패널 (40% 폭) -->
      <div
        class="w-5/12 flex flex-col bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden transition-all"
        :class="{ 'ring-2 ring-blue-400 border-blue-400 bg-blue-50/20': isDragging && canEdit }"
        @dragover.prevent="onDragOver"
        @dragleave.prevent="onDragLeave"
        @drop.prevent="onDrop"
      >
        <!-- 목록 헤더 -->
        <div class="p-2.5 bg-slate-50 border-b border-gray-200 flex justify-between items-center flex-shrink-0">
          <div class="flex items-center space-x-1.5 font-bold text-slate-700">
            <i class="fa fa-folder-open text-blue-500"></i>
            <span>파일 목록</span>
            <span class="text-xs bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded-full font-semibold">
              {{ fileList.length }}건
            </span>
          </div>
          <span class="text-xs text-gray-500">
            {{ canEdit ? `${allowedExtsLabel} 파일 드래그 업로드 가능 (최대 ${maxUploadSizeMb}MB)` : '행을 클릭하여 미리보기' }}
          </span>
        </div>

        <!-- 로딩 상태 -->
        <div v-if="isLoading" class="flex-1 flex flex-col items-center justify-center text-gray-500 space-y-2">
          <i class="fa fa-spinner fa-spin text-2xl text-blue-500"></i>
          <span>{{ isUploading ? '파일을 업로드하는 중...' : '파일 목록을 불러오는 중...' }}</span>
        </div>

        <!-- 파일 목록 테이블 -->
        <div v-else-if="fileList.length > 0" class="flex-1 overflow-y-auto">
          <table class="w-full border-collapse text-left">
            <thead class="bg-slate-100 sticky top-0 border-b border-gray-200 text-slate-600 text-xs select-none z-10">
              <tr>
                <th class="py-2 px-2 text-center font-bold w-12 border-r border-gray-200">No</th>
                <th class="py-2 px-3 font-bold border-r border-gray-200">파일명</th>
                <th class="py-2 px-2 text-center font-bold w-16 border-r border-gray-200">구분</th>
                <th class="py-2 px-2 text-right font-bold w-20 border-r border-gray-200">크기</th>
                <th class="py-2 px-2 text-center font-bold w-14 border-r border-gray-200">다운</th>
                <th v-if="canEdit" class="py-2 px-2 text-center font-bold w-14 text-red-500">삭제</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200 text-xs">
              <tr
                v-for="(item, index) in fileList"
                :key="item.FSEQ || index"
                @click="selectFile(item)"
                :class="[
                  'cursor-pointer transition-colors',
                  selectedFile && (selectedFile.FSEQ === item.FSEQ || selectedFile.FDISP === item.FDISP)
                    ? 'bg-blue-50/90 font-medium text-blue-900 border-l-4 border-blue-600'
                    : 'hover:bg-gray-50 text-slate-700'
                ]"
              >
                <td class="py-2 px-2 text-center text-gray-500 font-mono">
                  {{ item.FSEQ || (index + 1) }}
                </td>
                <td class="py-2 px-3 truncate max-w-xs" :title="item.FDISP">
                  <div class="flex items-center space-x-1.5">
                    <span class="text-base flex-shrink-0">
                      {{ getFileIcon(item) }}
                    </span>
                    <span class="truncate">{{ item.FDISP }}</span>
                  </div>
                  <div v-if="item.INS_DATETIME" class="text-[10px] text-gray-400 mt-0.5">
                    {{ formatDateStr(item.INS_DATETIME) }} <span v-if="item.INS_USID">({{ item.INS_USID }})</span>
                  </div>
                </td>
                <td class="py-2 px-2 text-center font-mono uppercase text-gray-500">
                  {{ item.FGBN??getExt(item) }}
                </td>
                <td class="py-2 px-2 text-right text-gray-500">
                  {{ formatFileSize(item.FSIZE) }}
                </td>
                <td class="py-2 px-2 text-center" @click.stop>
                  <button
                    @click="downloadFile(item)"
                    class="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded transition-colors"
                    title="다운로드"
                  >
                    <i class="fa fa-download"></i>
                  </button>
                </td>
                <td v-if="canEdit" class="py-2 px-2 text-center" @click.stop>
                  <button
                    @click="btnDeleteFile(item)"
                    class="p-1 text-red-500 hover:text-red-700 hover:bg-red-100 rounded transition-colors"
                    title="삭제"
                  >
                    <i class="fa fa-trash-alt"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- 빈 데이터 상태 -->
        <div v-else class="flex-1 flex flex-col items-center justify-center text-gray-400 space-y-3 p-4">
          <i class="fa fa-folder-open text-4xl text-gray-300"></i>
          <div class="text-center">
            <p class="font-medium text-slate-600">첨부된 파일이 없습니다.</p>
            <p v-if="canEdit" class="text-xs text-gray-400 mt-1">{{ allowedExtsLabel }} 파일을 드래그하여 놓거나 아래 버튼을 클릭하세요 (최대 {{ maxUploadSizeMb }}MB).</p>
          </div>
          <button
            v-if="canEdit"
            @click="triggerFileInput"
            class="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-semibold shadow transition-colors flex items-center gap-1.5 cursor-pointer"
          >
            <i class="fa fa-plus"></i> 파일 선택
          </button>
        </div>
      </div>

      <!-- 우측: 파일 미리보기/뷰어 패널 (60% 폭) -->
      <div class="w-7/12 flex flex-col bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <!-- 뷰어 상단 정보바 및 컨트롤 -->
        <div class="p-2 bg-slate-50 border-b border-gray-200 flex justify-between items-center flex-shrink-0">
          <div class="flex items-center space-x-2 min-w-0 pr-2">
            <span class="text-base flex-shrink-0">
              {{ selectedFile ? getFileIcon(selectedFile) : '📄' }}
            </span>
            <span class="font-bold text-slate-800 truncate text-xs sm:text-sm" :title="selectedFile?.FDISP">
              {{ selectedFile ? selectedFile.FDISP : '선택된 파일 없음' }}
            </span>
            <span v-if="selectedFile" class="text-[11px] bg-gray-200 text-gray-700 px-1.5 py-0.2 rounded font-mono uppercase">
              {{ getExt(selectedFile) }}
            </span>
          </div>

          <!-- 뷰어 컨트롤러 (이미지/공통) -->
          <div v-if="selectedFile" class="flex items-center space-x-1 flex-shrink-0">
            <!-- 이미지 전용 줌/회전 컨트롤 -->
            <template v-if="isImageFile(selectedFile)">
              <button @click="zoomIn" class="btn-tool" title="확대 (+)">
                <i class="fa fa-search-plus"></i>
              </button>
              <button @click="zoomOut" class="btn-tool" title="축소 (-)">
                <i class="fa fa-search-minus"></i>
              </button>
              <button @click="resetZoom" class="btn-tool text-xs" title="100% 원본">
                {{ Math.round(zoomScale * 100) }}%
              </button>
              <button @click="rotateRight" class="btn-tool" title="90도 회전">
                <i class="fa fa-redo"></i>
              </button>
              <span class="h-4 w-px bg-gray-300 mx-1"></span>
            </template>

            <!-- 새 창으로 열기 & 다운로드 버튼 -->
            <button
              @click="openInNewTab"
              class="btn-tool"
              title="새 창에서 원본 열기"
            >
              <i class="fa fa-external-link-alt text-blue-600"></i>
            </button>
            <button
              @click="downloadFile(selectedFile)"
              class="btn-tool"
              title="다운로드"
            >
              <i class="fa fa-download text-green-600"></i>
            </button>
          </div>
        </div>

        <!-- 뷰어 본문 캔버스 영역 -->
        <div class="flex-1 bg-slate-900/5 relative overflow-hidden flex items-center justify-center p-2">
          <!-- 1. 이미지 뷰어 -->
          <div
            v-if="selectedFile && isImageFile(selectedFile)"
            class="w-full h-full flex items-center justify-center overflow-auto checkerboard-bg rounded border border-gray-200"
          >
            <img
              :src="currentFileUrl"
              :alt="selectedFile.FDISP"
              :style="{
                transform: `scale(${zoomScale}) rotate(${rotationDeg}deg)`,
                transition: 'transform 0.15s ease-out',
                maxHeight: zoomScale === 1 ? '100%' : 'none',
                maxWidth: zoomScale === 1 ? '100%' : 'none',
              }"
              class="object-contain select-none shadow-md"
              @wheel.prevent="onImageWheel"
            />
          </div>

          <!-- 2. PDF 뷰어 -->
          <div
            v-else-if="selectedFile && isPdfFile(selectedFile)"
            class="w-full h-full bg-white rounded border border-gray-200 overflow-hidden relative flex flex-col"
          >
            <!-- 로딩 인디케이터 -->
            <div
              v-if="isPdfLoading"
              class="absolute inset-0 bg-white/90 z-10 flex flex-col items-center justify-center space-y-3"
            >
              <i class="fa fa-spinner fa-spin text-3xl text-blue-600"></i>
              <span class="text-sm font-medium text-gray-700">PDF 문서를 불러오는 중입니다...</span>
            </div>

            <!-- 로드 실패 상태 -->
            <div
              v-else-if="pdfLoadError || !pdfBlobUrl"
              class="w-full h-full flex flex-col items-center justify-center p-8 bg-gray-50 text-center"
            >
              <div class="text-5xl text-red-400 mb-3">
                <i class="fa fa-exclamation-triangle"></i>
              </div>
              <div class="font-bold text-gray-800 text-sm mb-1">
                PDF 미리보기를 불러오지 못했습니다.
              </div>
              <p class="text-xs text-gray-500 mb-4">
                브라우저 보안 설정 또는 네트워크 상태를 확인하시거나 다운로드하여 확인하세요.
              </p>
              <div class="flex items-center space-x-2">
                <button
                  @click="loadPdfBlob(selectedFile)"
                  class="px-3 py-1.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded text-xs font-medium transition-colors flex items-center space-x-1"
                >
                  <i class="fa fa-sync-alt"></i>
                  <span>다시 시도</span>
                </button>
                <button
                  @click="openInNewTab"
                  class="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-medium transition-colors flex items-center space-x-1"
                >
                  <i class="fa fa-external-link-alt"></i>
                  <span>새 창에서 열기</span>
                </button>
                <button
                  @click="downloadFile(selectedFile)"
                  class="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded text-xs font-medium transition-colors flex items-center space-x-1"
                >
                  <i class="fa fa-download"></i>
                  <span>다운로드</span>
                </button>
              </div>
            </div>

            <!-- 정상 렌더링 iframe -->
            <iframe
              v-else
              :src="pdfBlobUrl"
              class="w-full h-full border-0 flex-1"
              type="application/pdf"
              title="PDF 뷰어"
            ></iframe>
          </div>

          <!-- 3. 기타 일반 파일 뷰어 카드 -->
          <div
            v-else-if="selectedFile"
            class="flex flex-col items-center justify-center p-8 bg-white rounded-lg shadow-sm border border-gray-200 max-w-md text-center"
          >
            <div class="text-6xl mb-4 text-blue-500">
              <i class="fa fa-file-alt"></i>
            </div>
            <div class="font-bold text-slate-800 text-sm mb-1 break-all">
              {{ selectedFile.FDISP }}
            </div>
            <div class="text-xs text-gray-500 mb-4">
              {{ formatFileSize(selectedFile.FSIZE) }} • {{ (selectedFile.FEXT || '').toUpperCase() }} 파일
            </div>
            <p class="text-xs text-gray-400 mb-5">
              이 파일 형식은 브라우저 미리보기를 지원하지 않습니다. 다운로드하여 확인하세요.
            </p>
            <button
              @click="downloadFile(selectedFile)"
              class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium text-xs shadow transition-colors flex items-center space-x-2"
            >
              <i class="fa fa-download"></i>
              <span>파일 다운로드</span>
            </button>
          </div>

          <!-- 4. 파일 미선택 상태 -->
          <div v-else class="flex flex-col items-center justify-center text-gray-400 space-y-2">
            <i class="fa fa-eye-slash text-4xl text-gray-300"></i>
            <span>좌측 목록에서 미리볼 파일을 선택하세요.</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 모던 파일 삭제 확인 컨펌 모달 다이얼로그 -->
    <Teleport to="body">
      <transition
        enter-active-class="transition duration-200 ease-out"
        enter-from-class="opacity-0 scale-95"
        enter-to-class="opacity-100 scale-100"
        leave-active-class="transition duration-150 ease-in"
        leave-from-class="opacity-100 scale-100"
        leave-to-class="opacity-0 scale-95"
      >
        <div
          v-if="isConfirmModalOpen"
          class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm"
          @click.self="cancelDelete"
        >
          <div
            class="bg-white rounded-2xl shadow-2xl border border-gray-100 max-w-sm w-full p-6 text-center transform transition-all"
            role="dialog"
            aria-modal="true"
          >
            <!-- 경고 아이콘 -->
            <div class="mx-auto flex items-center justify-center w-14 h-14 rounded-full bg-red-50 text-red-500 mb-4 ring-8 ring-red-50/50">
              <i class="fa fa-trash-alt text-2xl"></i>
            </div>

            <h3 class="text-base font-bold text-slate-800 mb-2">파일 삭제 확인</h3>
            
            <p class="text-xs text-gray-500 mb-4">
              선택한 파일을 정말 삭제하시겠습니까?<br />
              <span class="text-red-500 font-medium">삭제된 파일은 복구할 수 없습니다.</span>
            </p>

            <!-- 파일 정보 카드 -->
            <div v-if="deleteTargetFile" class="bg-slate-50 border border-slate-200 rounded-xl p-3 mb-5 text-left flex items-center space-x-3">
              <div class="text-2xl flex-shrink-0">
                {{ getFileIcon(deleteTargetFile) }}
              </div>
              <div class="min-w-0 flex-1">
                <p class="text-xs font-semibold text-slate-800 truncate" :title="deleteTargetFile.FDISP">
                  {{ deleteTargetFile.FDISP }}
                </p>
                <p class="text-[11px] text-gray-400 mt-0.5 font-mono">
                  {{ formatFileSize(deleteTargetFile.FSIZE) }} • {{ getExt(deleteTargetFile).toUpperCase() }}
                </p>
              </div>
            </div>

            <!-- 버튼 영역 -->
            <div class="flex space-x-2.5">
              <button
                type="button"
                @click="cancelDelete"
                class="flex-1 py-2.5 px-4 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl text-xs transition-colors cursor-pointer"
              >
                취소
              </button>
              <button
                type="button"
                @click="confirmDelete"
                class="flex-1 py-2.5 px-4 bg-red-600 hover:bg-red-700 active:bg-red-800 text-white font-semibold rounded-xl text-xs shadow-md shadow-red-200 transition-all flex items-center justify-center gap-1.5 cursor-pointer"
              >
                <i class="fa fa-trash-alt text-xs"></i>
                <span>삭제</span>
              </button>
            </div>
          </div>
        </div>
      </transition>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from "vue";
import { useRoute } from "vue-router";
import { saveAs } from "file-saver";
import { notify } from "@kyvg/vue3-notification";
import { useUserStore } from "@/store/userStore";

const route = useRoute();
const userStore = useUserStore();

// 기본 파라미터 상태
const defaultCid = process.env.VUE_APP_CID;
const maxUploadSizeMb = Number(process.env.VUE_APP_MAX_UPLOAD_SIZE_MB) || 15;
const maxFileSizeBytes = maxUploadSizeMb * 1024 * 1024;

// 업로드 가능 확장자 설정 (환경변수)
const allowedExtsRaw = process.env.VUE_APP_ALLOWED_FILE_EXTENSIONS || "pdf,jpg,png";
const allowedExts = computed(() => {
  return allowedExtsRaw
    .split(",")
    .map((ext) => ext.trim().toLowerCase().replace(/^\./, ""))
    .filter(Boolean);
});
const acceptFileExtensions = computed(() => {
  return allowedExts.value.map((ext) => `.${ext}`).join(",");
});
const allowedExtsLabel = computed(() => {
  return allowedExts.value.map((ext) => ext.toUpperCase()).join(", ");
});

const currentGuid = ref("");
const customTitle = ref("");
const companyId = ref(defaultCid);
const fileKey = ref("");
const fileMode = ref("");
const fileTyp = ref("ORDER");
const fileGbn = ref("성적서");

// 파일 목록 & 선택된 파일 상태
const fileList = ref([]);
const selectedFile = ref(null);
const isLoading = ref(false);

// PDF 미리보기 관련 상태
const pdfBlobUrl = ref("");
const isPdfLoading = ref(false);
const pdfLoadError = ref(false);

// 업로드 관련 상태
const fileInputRef = ref(null);
const isDragging = ref(false);
const isUploading = ref(false);

// 삭제 확인 모달 상태
const isConfirmModalOpen = ref(false);
const deleteTargetFile = ref(null);

// 업로드/삭제 가능 모드 여부 (OrderList 등에서 fmode='EDIT'로 열었을 때)
const canEdit = computed(() => {
  return String(fileMode.value).trim().toUpperCase() === "EDIT";
});

// 이미지 조작 상태
const zoomScale = ref(1);
const rotationDeg = ref(0);

// 타이틀 바 텍스트
const titleText = computed(() => {
  if (customTitle.value) return customTitle.value;
  return "첨부파일 목록 및 뷰어";
});

// 선택된 파일의 다운로드/미리보기 URL
const currentFileUrl = computed(() => {
  if (!selectedFile.value) return "";
  return getFileUrl(selectedFile.value);
});

// 파일 URL 생성 함수
function getFileUrl(file) {
  if (!file) return "";
  // 로컬에서 방금 업로드된 파일 객체가 있는 경우 Blob URL 우선 사용
  if (file._fileObj) {
    return URL.createObjectURL(file._fileObj);
  }
  const cid = companyId.value || defaultCid;
  const guid = file.FGUID || currentGuid.value;
  const seq = file.FSEQ || 1;
  return `/fileapi/download/${cid}/${guid}/${seq}`;
}

// 파일 선택 다이얼로그 호출
function triggerFileInput() {
  if (fileInputRef.value) {
    fileInputRef.value.click();
  }
}

// 파일 선택 이벤트 핸들러
function handleFileSelected(event) {
  const files = Array.from(event.target.files || []);
  if (files.length > 0) {
    uploadFiles(files);
  }
  event.target.value = "";
}

// 드래그 앤 드롭 이벤트 핸들러
function onDragOver(e) {
  if (!canEdit.value) return;
  isDragging.value = true;
}

function onDragLeave(e) {
  if (!canEdit.value) return;
  isDragging.value = false;
}

function onDrop(e) {
  if (!canEdit.value) return;
  isDragging.value = false;
  const files = Array.from(e.dataTransfer?.files || []);
  if (files.length > 0) {
    uploadFiles(files);
  }
}

// 파일 업로드 API 호출 함수
async function uploadFiles(files) {
  if (!files || files.length === 0) return;

  // 1. 파일 확장자 검증 (환경변수 설정값 기준)
  const invalidFiles = files.filter((f) => {
    const ext = f.name ? f.name.substring(f.name.lastIndexOf(".") + 1).toLowerCase() : "";
    return !allowedExts.value.includes(ext);
  });
  if (invalidFiles.length > 0) {
    notify({
      title: "업로드 제한",
      text: `${allowedExtsLabel.value} 파일만 업로드할 수 있습니다. [${invalidFiles.map((f) => f.name).join(", ")}]`,
      type: "warn",
    });
    return;
  }

  // 2. 최대 파일 크기 검증 (환경변수 설정값 기준)
  const oversizedFiles = files.filter((f) => f.size > maxFileSizeBytes);
  if (oversizedFiles.length > 0) {
    notify({
      title: "용량 초과",
      text: `파일 크기는 최대 ${maxUploadSizeMb}MB까지 업로드 가능합니다. [${oversizedFiles.map((f) => f.name).join(", ")}]`,
      type: "warn",
    });
    return;
  }

  isUploading.value = true;
  isLoading.value = true;

  try {
    const cid = companyId.value || defaultCid;
    const guid = currentGuid.value ? String(currentGuid.value).trim() : "";

    // Route: [Route("upload/{cid}/{guid}")] 또는 [Route("upload/{cid}")]
    const uploadUrl = guid
      ? `/fileapi/upload/${encodeURIComponent(cid)}/${encodeURIComponent(guid)}`
      : `/fileapi/upload/${encodeURIComponent(cid)}`;

    const formData = new FormData();

    // 필수 파라미터 USID (업로드 사용자 ID)
    const usid = userStore.userId || userStore.userVendCd;
    formData.append("USID", usid);

    // 각 파일 및 메타데이터 추가 (동일한 접미사 매핑 규칙: FILE0, TYP0, REMARK0, FGBN0...)
    files.forEach((file, index) => {
      formData.append(`FILE${index}`, file);
      formData.append(`TYP${index}`, fileTyp.value);
      formData.append(`FGBN${index}`, fileGbn.value);
      formData.append(`REMARK${index}`, fileKey.value);
      formData.append(`CID${index}`, cid);
    });

    const response = await fetch(uploadUrl, {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      const errText = await response.text();
      throw new Error(errText || `업로드 실패 (HTTP ${response.status})`);
    }

    const resData = await response.json().catch(() => null);
    // console.log("업로드 응답:", resData);

    // 서버 응답에서 GUID 추출 및 갱신
    if (resData) {
      if (Array.isArray(resData) && resData.length > 0 && resData[0].FGUID) {
        currentGuid.value = resData[0].FGUID;
      }
    }

    notify({
      title: "업로드 성공",
      text: `${files.length}건의 파일이 업로드되었습니다.`,
      type: "success",
    });

    // 업로드 후 최신 파일 목록 재조회
    await fetchFileList();

    // 부모 창에 업로드 완료 정보 전달 (GUID 및 fkey 갱신용)
    if (window.opener && !window.opener.closed) {
      window.opener.postMessage(
        {
          type: "FILE_UPLOAD_SUCCESS",
          guid: currentGuid.value,
          fkey: fileKey.value,
          fmode: fileMode.value,
          count: files.length,
        },
        "*"
      );
    }
  } catch (error) {
    console.error("파일 업로드 오류:", error);
    notify({
      title: "업로드 실패",
      text: error.message || "파일 업로드 중 오류가 발생했습니다.",
      type: "error",
    });
  } finally {
    isUploading.value = false;
    isLoading.value = false;
  }
}

// 파일 삭제 버튼 클릭 (모달 열기)
function btnDeleteFile(file) {
  deleteTargetFile.value = file;
  isConfirmModalOpen.value = true;
}

// 파일 삭제 취소
function cancelDelete() {
  isConfirmModalOpen.value = false;
  deleteTargetFile.value = null;
}

// 파일 삭제 확인 실행
async function confirmDelete() {
  const file = deleteTargetFile.value;
  isConfirmModalOpen.value = false;
  deleteTargetFile.value = null;
  if (file) {
    await deleteFile(file);
  }
}

// 파일 삭제 API 호출 함수
async function deleteFile(file) {
  if (!file) return;

  isLoading.value = true;
  try {
    const cid = companyId.value || defaultCid;
    const guid = file.FGUID || currentGuid.value;
    const seq = file.FSEQ;

    if (!guid || seq === undefined || seq === null) {
      throw new Error("삭제할 파일의 식별자(GUID/SEQ)가 없습니다.");
    }

    const deleteUrl = `/fileapi/delete/${cid}/${guid}/${seq}`;

    const formData = new FormData();

    // 필수 파라미터 USID (업로드 사용자 ID)
    const usid = userStore.userId || userStore.userVendCd;
    formData.append("USID", usid);

    const response = await fetch(deleteUrl, {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      const errText = await response.text();
      throw new Error(errText || `파일 삭제 실패 (HTTP ${response.status})`);
    }

    notify({
      title: "삭제 성공",
      text: `'${file.FDISP}' 파일이 삭제되었습니다.`,
      type: "success",
    });

    // 최신 파일 목록 재조회
    await fetchFileList();

    // 파일이 0건이 되면 FGUID를 공백으로 초기화
    if (fileList.value.length === 0) {
      currentGuid.value = "";
      selectedFile.value = null;
    }

    // 부모 창에 파일 삭제 정보 전달
    if (window.opener && !window.opener.closed) {
      window.opener.postMessage(
        {
          type: "FILE_DELETE_SUCCESS",
          guid: currentGuid.value,
          fkey: fileKey.value,
          fmode: fileMode.value,
          deletedSeq: file.FSEQ,
          remainingCount: fileList.value.length,
        },
        "*"
      );
    }
  } catch (error) {
    console.error("파일 삭제 오류:", error);
    notify({
      title: "삭제 실패",
      text: error.message || "파일 삭제 중 오류가 발생했습니다.",
      type: "error",
    });
  } finally {
    isLoading.value = false;
  }
}

// 파일 확장자 추출 헬퍼 (FEXT 우선, 없으면 FDISP/FNAME에서 추출)
function getExt(fileOrExt = "") {
  if (!fileOrExt) return "";
  let str = "";
  if (typeof fileOrExt === "object") {
    str = String(fileOrExt.FEXT || "");
    if (!str && fileOrExt.FDISP) {
      const parts = String(fileOrExt.FDISP).split(".");
      if (parts.length > 1) str = parts.pop();
    }
    if (!str && fileOrExt.FNAME) {
      const parts = String(fileOrExt.FNAME).split(".");
      if (parts.length > 1) str = parts.pop();
    }
  } else {
    str = String(fileOrExt);
  }
  return str.toLowerCase().trim().replace(/^\./, "");
}

// 확장자별 아이콘 매핑
function getFileIcon(fileOrExt = "") {
  const e = getExt(fileOrExt);
  if (["png", "jpg", "jpeg", "gif", "bmp", "webp", "svg"].includes(e)) {
    return "🖼️";
  }
  if (e === "pdf") {
    return "📄";
  }
  if (["xls", "xlsx", "csv"].includes(e)) {
    return "📊";
  }
  if (["doc", "docx", "hwp", "txt"].includes(e)) {
    return "📝";
  }
  if (["zip", "rar", "7z", "tar", "gz"].includes(e)) {
    return "🗜️";
  }
  return "📁";
}

// 이미지 파일 여부 판별
function isImageFile(fileOrExt = "") {
  const e = getExt(fileOrExt);
  return ["png", "jpg", "jpeg", "gif", "bmp", "webp", "svg"].includes(e);
}

// PDF 파일 여부 판별
function isPdfFile(fileOrExt = "") {
  const e = getExt(fileOrExt);
  return e === "pdf";
}

// PDF Blob URL 생성 함수 (Content-Disposition 무력화 및 정확한 MIME 타입 보장)
async function loadPdfBlob(file) {
  if (pdfBlobUrl.value) {
    URL.revokeObjectURL(pdfBlobUrl.value);
    pdfBlobUrl.value = "";
  }
  pdfLoadError.value = false;

  if (!file) return;

  if (file._fileObj) {
    const pdfBlob = new Blob([file._fileObj], { type: "application/pdf" });
    pdfBlobUrl.value = URL.createObjectURL(pdfBlob);
    return;
  }

  isPdfLoading.value = true;
  try {
    const rawUrl = getFileUrl(file);
    const response = await fetch(rawUrl);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    const blob = await response.blob();
    const pdfBlob = new Blob([blob], { type: "application/pdf" });
    pdfBlobUrl.value = URL.createObjectURL(pdfBlob);
  } catch (err) {
    console.error("PDF Blob 로드 실패:", err);
    pdfLoadError.value = true;
  } finally {
    isPdfLoading.value = false;
  }
}

// 날짜 포맷 (YYYYMMDDHHmmss -> YYYY-MM-DD HH:mm)
function formatDateStr(str) {
  if (!str || str.length < 8) return str || "";
  const y = str.substring(0, 4);
  const m = str.substring(4, 6);
  const d = str.substring(6, 8);
  if (str.length >= 12) {
    const hh = str.substring(8, 10);
    const mm = str.substring(10, 12);
    return `${y}-${m}-${d} ${hh}:${mm}`;
  }
  return `${y}-${m}-${d}`;
}

// 파일 용량 포맷팅
function formatFileSize(bytes) {
  const size = Number(bytes) || 0;
  if (size === 0) return "-";
  if (size < 1024) return size + " B";
  if (size < 1024 * 1024) return (size / 1024).toFixed(1) + " KB";
  return (size / (1024 * 1024)).toFixed(1) + " MB";
}

// 파일 선택 핸들러
function selectFile(file) {
  if (selectedFile.value === file && isPdfFile(file) && (pdfLoadError.value || !pdfBlobUrl.value)) {
    loadPdfBlob(file);
    return;
  }
  selectedFile.value = file;
  zoomScale.value = 1;
  rotationDeg.value = 0;
}

// 파일 목록 API 호출
async function fetchFileList() {
  if (!currentGuid.value || String(currentGuid.value).trim() === "") {
    fileList.value = [];
    selectedFile.value = null;
    return;
  }

  isLoading.value = true;
  const cid = companyId.value || defaultCid;
  const apiUrl = `/fileapi/select/${cid}/${currentGuid.value}`;

  try {
    const response = await fetch(apiUrl);
    if (!response.ok) {
      throw new Error(`서버 응답 오류 (HTTP ${response.status})`);
    }
    const data = await response.json();
    // console.log("FList fetchFileList data:", data);

    if (Array.isArray(data)) {
      fileList.value = data;
      if (data.length > 0) {
        // 첫 번째 파일 자동 선택
        selectFile(data[0]);
      } else {
        selectedFile.value = null;
      }
    } else {
      fileList.value = [];
      selectedFile.value = null;
    }
  } catch (err) {
    console.error("파일 목록 조회 오류:", err);
    notify({
      title: "조회 실패",
      text: "첨부파일 목록을 가져오는 데 실패했습니다.",
      type: "error",
    });
    fileList.value = [];
    selectedFile.value = null;
  } finally {
    isLoading.value = false;
  }
}

// 개별 파일 다운로드
async function downloadFile(file) {
  if (!file) return;
  try {
    const url = getFileUrl(file);
    const response = await fetch(url);
    const blob = await response.blob();
    saveAs(blob, file.FDISP || `file_${file.FSEQ}${file.FEXT || ''}`);
  } catch (err) {
    console.warn("Blob 다운로드 실패, 직접 링크로 대체:", err);
    window.open(getFileUrl(file), "_blank");
  }
}

// 전체 파일 일괄 다운로드
async function downloadAllFiles() {
  if (!fileList.value || fileList.value.length === 0) return;
  for (const file of fileList.value) {
    await downloadFile(file);
  }
}

// 새 창에서 원본 열기
function openInNewTab() {
  if (selectedFile.value) {
    if (isPdfFile(selectedFile.value) && pdfBlobUrl.value) {
      window.open(pdfBlobUrl.value, "_blank");
    } else {
      window.open(currentFileUrl.value, "_blank");
    }
  }
}

// 이미지 확대/축소 및 회전
function zoomIn() {
  if (zoomScale.value < 4) zoomScale.value = parseFloat((zoomScale.value + 0.25).toFixed(2));
}

function zoomOut() {
  if (zoomScale.value > 0.3) zoomScale.value = parseFloat((zoomScale.value - 0.25).toFixed(2));
}

function resetZoom() {
  zoomScale.value = 1;
  rotationDeg.value = 0;
}

function rotateRight() {
  rotationDeg.value = (rotationDeg.value + 90) % 360;
}

function onImageWheel(e) {
  if (e.deltaY < 0) {
    zoomIn();
  } else {
    zoomOut();
  }
}

// 창 닫기
function btnClose() {
  window.close();
}

// 팝업 데이터 초기화 및 갱신
function initDataFromRoute() {
  currentGuid.value = route.query.guid ? String(route.query.guid).trim() : "";
  customTitle.value = route.query.title ? String(route.query.title).trim() : "";
  companyId.value = route.query.cid ? String(route.query.cid).trim() : defaultCid;
  fileKey.value = route.query.fkey
    ? String(route.query.fkey).trim()
    : `${route.query.rdoGubun || ""}${route.query.pos || ""}`;
  fileMode.value = route.query.fmode
    ? String(route.query.fmode).trim()
    : (route.query.ftype ? String(route.query.ftype).trim() : "");
  fileTyp.value = route.query.ftyp ? String(route.query.ftyp).trim() : "";
  fileGbn.value = route.query.fgbn ? String(route.query.fgbn).trim() : "";

  if (currentGuid.value) {
    fetchFileList();
  } else {
    fileList.value = [];
    selectedFile.value = null;
  }
}

// 선택된 파일 감시 -> PDF인 경우 Blob 로드
watch(
  selectedFile,
  (newFile) => {
    zoomScale.value = 1;
    rotationDeg.value = 0;
    if (newFile && isPdfFile(newFile)) {
      loadPdfBlob(newFile);
    } else {
      if (pdfBlobUrl.value) {
        URL.revokeObjectURL(pdfBlobUrl.value);
        pdfBlobUrl.value = "";
      }
    }
  },
  { immediate: true }
);

// 부모 창으로부터 전달받는 postMessage 리스너
function handleMessage(event) {
  if (event.data && event.data.type === "UPDATE_FLIST_DATA") {
    if (event.data.guid) {
      currentGuid.value = String(event.data.guid).trim();
    }
    if (event.data.title) {
      customTitle.value = String(event.data.title).trim();
    }
    if (event.data.cid) {
      companyId.value = String(event.data.cid).trim();
    }
    fetchFileList();
  }
}

onMounted(() => {
  initDataFromRoute();
  window.addEventListener("message", handleMessage);
});

onUnmounted(() => {
  window.removeEventListener("message", handleMessage);
  if (pdfBlobUrl.value) {
    URL.revokeObjectURL(pdfBlobUrl.value);
    pdfBlobUrl.value = "";
  }
});

// 라우트 쿼리 변경 감지 (팝업 창이 열려있는 상태에서 다른 행 클릭 시 자동 갱신)
watch(
  () => route.query,
  () => {
    initDataFromRoute();
  },
  { deep: true }
);
</script>

<style scoped>
.btn-action {
  @apply px-3 py-1 bg-white border border-gray-300 rounded font-bold text-slate-700 hover:bg-slate-50 transition-colors shadow-xs flex items-center text-xs;
}

.btn-tool {
  @apply p-1.5 text-gray-600 hover:text-blue-600 hover:bg-gray-200 rounded transition-colors flex items-center justify-center text-xs;
}

.checkerboard-bg {
  background-color: #f8fafc;
  background-image: linear-gradient(45deg, #e2e8f0 25%, transparent 25%),
    linear-gradient(-45deg, #e2e8f0 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, #e2e8f0 75%),
    linear-gradient(-45deg, transparent 75%, #e2e8f0 75%);
  background-size: 20px 20px;
  background-position: 0 0, 0 10px, 10px -10px, -10px 0px;
}
</style>
